import { useEffect, useState, useRef } from 'react';
import {
  BankAccountApi,
  BankAccountDetails,
  BankAccountUsage,
  CardInfo,
  Classification,
  Currency,
  FilterOption,
  PartialPaymentTransaction,
  PaymentApi,
  PaymentDetails,
  PaymentOption,
  PaymentStatus,
  PmpProjection,
  RefType,
} from '@commerce-payment-webcomponents/sdk';
import {
  AlertMessage,
  AlertMessageWidth,
  IconStyle,
  Theme,
  useTranslation,
  useSession,
} from '@nextgen-web-framework/all';
import { PaymentDetail } from '../common/payment-detail/PaymentDetail';
import WithCMSImage from '../../common/hoc/WithCMSImage';
import { PaymentLoader, PaymentLoaderType } from '../payment-loader/PaymentLoader';
import { PaymentSummaryProps } from './types/propsTypes';
import styles from './PaymentSummary.module.scss';

export const PaymentSummary = ({
  paymentId,
  orderId,
  cardId,
  hidePaymentSummaryAmount,
  refType = RefType.Order,
  enableDirectDebit = false,
  filterCategory,
  filterType,
}: PaymentSummaryProps) => {
  const [filterOption, setFilterOption] = useState<FilterOption>({} as FilterOption);
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
  const [showErrorCard, setShowErrorCard] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [retryFailMessage, setRetryFailMessage] = useState(false);
  const [cardInfo, setCardInfo] = useState<{
    cardNo: string;
    cardSchemeCode: string;
  }>({
    cardNo: '',
    cardSchemeCode: '',
  });
  const ref = useRef(0);
  const t = useTranslation();
  const { isActor } = useSession();

  if (!orderId && !paymentId && !enableDirectDebit && !cardId && !filterCategory) {
    throw new Error('Please pass OrderId OR PaymentId OR filterCategory');
  }
  const [transactions, setTransactions] = useState<PartialPaymentTransaction[]>([]);
  const [bankAccountDetails, setBankAccountDetails] = useState({} as BankAccountDetails);

  const fetchPaymentOption = () => {
    const tempFilterOptions =
      filterCategory === 'RENEWAL'
        ? {
            orderType: RefType.AutoRenewal,
            orderTotal: {
              value: 1,
              currency: Currency.Thb,
            },
            classification: isActor?.() ? [Classification.AutoRenewalImpersonation] : undefined,
          }
        : filterOption;

    PaymentApi.getPaymentOptions(tempFilterOptions, PmpProjection.All)
      .then((paymentOptions) => {
        setPaymentOptions(paymentOptions);
      })
      .catch(() => setShowErrorCard(true));
  };
  useEffect(() => {
    if (filterOption.orderType || filterCategory === 'RENEWAL') {
      fetchPaymentOption();
    }
  }, [filterOption, filterCategory]);

  const updateFiltersAndTransactions = (data: PaymentDetails) => {
    setFilterOption({
      orderTotal: data?.amount,
      orderType: data?.refType,
    });
    setTransactions(data?.transactions);
  };

  const fetchPayment = () => {
    PaymentApi.getPaymentById(paymentId as string)
      .then((data) => {
        //condition for reftype subscription
        if (data?.refType === RefType.Subscription) {
          if (data?.status === PaymentStatus.Verified) {
            updateFiltersAndTransactions(data);
            setShowLoader(false);
          } else {
            //logic to recall API
            if (!ref.current) {
              setTimeout(() => {
                ref.current = 1;
                fetchPayment();
              }, 3000);
            } else {
              setShowLoader(false);
              setShowErrorCard(true);
              setRetryFailMessage(true);
            }
          }
        } else {
          updateFiltersAndTransactions(data);
          setShowLoader(false);
        }
      })
      .catch(() => {
        setShowLoader(false);
        setShowErrorCard(true);
      });
  };

  const fetchPaymentByOrderId = () => {
    PaymentApi.getPayments(orderId as string, refType as string)
      .then((payments) => {
        updateFiltersAndTransactions(payments?.[0]);
        setShowLoader(false);
      })
      .catch(() => {
        setShowLoader(false);
        setShowErrorCard(true);
      });
  };

  const fetchPaymentByCardId = () => {
    PaymentApi.getPaymentByCardId(cardId as string)
      .then((cardInfo) => {
        const { cardNo, cardSchemeCode } = cardInfo;
        setCardInfo({ cardNo: cardNo, cardSchemeCode: cardSchemeCode });
        setShowLoader(false);
      })
      .catch(() => {
        setShowLoader(false);
        setShowErrorCard(true);
      });
  };

  const fetchRenewalCard = (filterCategory?: string) => {
    PaymentApi.getPaymentForRenewal(filterCategory as string)
      .then((cardInfo: CardInfo[]) => {
        if (cardInfo?.length) {
          const [cardDetails] = cardInfo;
          const { cardNo, cardSchemeCode } = cardDetails;

          setCardInfo({ cardNo: cardNo, cardSchemeCode: cardSchemeCode });
        }
        setShowLoader(false);
      })
      .catch(() => {
        setShowLoader(false);
        setShowErrorCard(true);
      });
  };

  const fetchBankAccount = () => {
    BankAccountApi.getAccountBasedBanks(BankAccountUsage.Renewal).then((result) => {
      if (result?.length) {
        setBankAccountDetails(result?.[0]);
      }
      setShowLoader(false);
    });
  };

  // If we have paymentId then use PaymentId to fetch Transactions
  useEffect(() => {
    setShowLoader(true);
    if (orderId) {
      fetchPaymentByOrderId();
    } else if (paymentId) {
      fetchPayment();
    } else if (cardId) {
      fetchPaymentByCardId();
    } else if (filterCategory) {
      filterType === 'card' ? fetchRenewalCard(filterCategory) : fetchBankAccount();
    } else if (enableDirectDebit) {
      setShowLoader(false);
    }
  }, [orderId, paymentId, cardId, filterCategory]);

  return (
    <>
      {(enableDirectDebit || paymentId || orderId || cardId || filterCategory) && (
        <WithCMSImage>
          {showLoader ? (
            <PaymentLoader loaderType={PaymentLoaderType.PAYMENT_SUMMARY} />
          ) : (
            <PaymentDetail
              orderType={filterOption.orderType}
              paymentOptions={paymentOptions}
              transactions={transactions}
              hidePaymentSummaryAmount={hidePaymentSummaryAmount}
              enableDirectDebit={enableDirectDebit}
              isCardId={Boolean(cardId ?? filterCategory)}
              cardInfo={cardInfo}
              filterCategory={filterCategory}
              filterType={filterType}
              bankAccountDetails={bankAccountDetails}
            />
          )}

          {
            // Display error card if no data
            showErrorCard && (
              <AlertMessage
                width={AlertMessageWidth.FULL_WIDTH}
                icon="square-exclamation"
                iconStyle={IconStyle.REGULAR}
                className={styles['instalment-plan-link']}
                content={{
                  heading: retryFailMessage ? t('payment.summary.retryErrorCard') : t('payment.summary.errorCard'),
                }}
                hasIcon
                theme={Theme.ERROR}
              />
            )
          }
        </WithCMSImage>
      )}
    </>
  );
};
export type { PaymentSummaryProps };
